import {NumberInput, Select} from '@mantine/core';
import {Grid} from 'src/design/atoms/Grid';
import React from 'react';
import {renderFieldLabel} from 'src/app/forms/renderFieldLabel';
import type {IFormFieldV2} from 'src/types/FormV2';

export interface MeasurementValue {
  magnitude: number | '';
  unit: string;
}

export interface FormMeasurementFieldV2Props {
  field: IFormFieldV2;
  value: MeasurementValue | '' | undefined;
  error?: string;
  onChange: (name: string, value: MeasurementValue) => void;
}

/**
 * Renders a magnitude `NumberInput` paired with a unit `Select`. When the
 * field defines exactly one allowed unit, the unit picker collapses into a
 * fixed `NumberInput` suffix instead — there is nothing to choose. See
 * `IFormFieldV2.measurementOptions` for the value-shape rationale
 * (magnitude/unit object, matching asset/person/project).
 */
export const FormMeasurementFieldV2: React.FC<FormMeasurementFieldV2Props> = ({
  field,
  value,
  error,
  onChange,
}) => {
  const units = field.measurementOptions?.units ?? [];
  const magnitude = value && value.magnitude !== '' ? value.magnitude : '';
  const unit = value ? value.unit : units[0] ?? '';
  const singleUnit = units.length === 1 ? units[0] : undefined;
  const minValue = field.measurementOptions?.minValue;
  const maxValue = field.measurementOptions?.maxValue;

  const rangeHelper = (() => {
    const unitSuffix = singleUnit ? ` ${singleUnit}` : '';
    if (minValue != null && maxValue != null) {
      return `Range: ${minValue}–${maxValue}${unitSuffix}`;
    }
    if (minValue != null) {
      return `Min ${minValue}${unitSuffix}`;
    }
    if (maxValue != null) {
      return `Max ${maxValue}${unitSuffix}`;
    }
    return undefined;
  })();

  const description = [field.description, rangeHelper].filter(Boolean).join(' — ');

  const handleMagnitudeChange = (val: number | string) => {
    onChange(field.name, {
      magnitude: typeof val === 'number' ? val : '',
      unit: singleUnit ?? unit,
    });
  };

  const handleUnitChange = (val: string | null) => {
    onChange(field.name, {
      magnitude,
      unit: val ?? '',
    });
  };

  if (singleUnit) {
    return (
      <NumberInput
        data-testid={`form-field-${field.name}`}
        label={renderFieldLabel(field)}
        description={description || undefined}
        error={error}
        value={magnitude}
        onChange={handleMagnitudeChange}
        min={field.measurementOptions?.minValue}
        max={field.measurementOptions?.maxValue}
        decimalScale={field.measurementOptions?.decimalPlaces}
        suffix={` ${singleUnit}`}
        withAsterisk={field.required}
        placeholder={`Enter ${field.label.toLowerCase()}`}
        disabled={field.disabled}
      />
    );
  }

  return (
    <Grid gutter="xs">
      <Grid.Col span={8}>
        <NumberInput
          data-testid={`form-field-${field.name}`}
          label={renderFieldLabel(field)}
          description={description || undefined}
          error={error}
          value={magnitude}
          onChange={handleMagnitudeChange}
          min={field.measurementOptions?.minValue}
          max={field.measurementOptions?.maxValue}
          decimalScale={field.measurementOptions?.decimalPlaces}
          withAsterisk={field.required}
          placeholder={`Enter ${field.label.toLowerCase()}`}
          disabled={field.disabled}
        />
      </Grid.Col>
      <Grid.Col span={4}>
        <Select
          data-testid={`form-field-${field.name}-unit`}
          label="Unit"
          // Blank description mirrors the magnitude input's help-text row so
          // the two inputs stay vertically aligned.
          description={description ? '\u00A0' : undefined}
          value={unit || null}
          onChange={handleUnitChange}
          data={units}
          placeholder="Unit"
          disabled={field.disabled}
          clearable={false}
        />
      </Grid.Col>
    </Grid>
  );
};
